// src/components/Navbar.js
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar as BSNavbar, Nav, NavDropdown, Container } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import { getGenera } from '../services/api';
import { ShoppingCart, LogOut, LogIn, UserRound, UserRoundPlus, Menu, X, Settings } from 'lucide-react';
// I-03 R6 · S3-06.3 — the desktop dock's toggle. It renders itself only
// on facility routes and fires an event; the navbar holds no dock state.
import FroggerDockButton from './froggerAI/FroggerDockButton';
import NavbarSpacer from './layout/NavbarSpacer';
import { useCart } from "../context/CartContext";
import { useFacilityConsole } from '../context/FacilityConsoleContext';
import FacilityConsoleDrawer from './Drawers/FacilityConsoleDrawer';
import { useFocusTrap } from '../hooks/useFocusTrap';
import NavbarSearch from './NavbarSearch';
// R-NOTIFY §5.5 — the bell beside the cart (its count is read ONCE here
// and handed to both icon rows).
import NotificationBell, { useUnreadNotifications } from './Notifications/NotificationBell';

import '../styles/navbar.css';
import '../styles/global.css';

const Navbar = () => {
    const { isAuthenticated, logout } = useContext(AuthContext);
    const { getTotalQuantity } = useCart();
    const { hasFacilityAccess, selectedFacilityId, toggleDrawer: toggleFacilityDrawer, drawerOpen: facilityDrawerOpen } = useFacilityConsole();
    const bellFacilityId = (isAuthenticated && hasFacilityAccess && selectedFacilityId) ? String(selectedFacilityId) : null;
    const unreadNotifications = useUnreadNotifications(bellFacilityId);
    const navigate = useNavigate();

    const [frogGenera, setFrogGenera] = useState([]);
    const [geckoGenera, setGeckoGenera] = useState([]);
    const [userDrawerOpen, setUserDrawerOpen] = useState(false);
    const drawerRef = useFocusTrap(userDrawerOpen);

    // Add/remove body class when drawer opens/closes
    useEffect(() => {
        if (userDrawerOpen) {
            document.body.classList.add('drawer-account-open');
        } else {
            document.body.classList.remove('drawer-account-open');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('drawer-account-open');
        };
    }, [userDrawerOpen]);

    // Handle ESC key to close drawer
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && userDrawerOpen) {
                setUserDrawerOpen(false);
            }
        };

        if (userDrawerOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [userDrawerOpen]);

    // Legacy right drawer removed - now only using FacilityConsoleDrawer

    // Log MyAccountDrawer status for diagnostics
    useEffect(() => {
      if (isAuthenticated) {
        console.debug('[MyAccountDrawer] mounted');
      }
    }, [isAuthenticated]);

    // Legacy facility fetching removed - now handled by FacilityConsoleContext

    // I-03 S3-01.7 PERF — ONE call for both menus.
    //
    // This was two getProducts() calls (speciesType 0, then 3), each
    // pulling a fully serialized catalog — images, variations, prices,
    // promotions, lots — so the navbar could read `p.genus` off each
    // row and throw the rest away. They fired on EVERY page in the app,
    // which is why the operator saw two /store/products/ requests on the
    // Cuttings page, a page with no products on it. `store/genera/`
    // returns the distinct genus names directly.
    //
    // Failing silent-with-empty-arrays stays the correct UX (downgraded
    // from console.error 2026-07-15 during S12 triage): the dropdowns
    // are a nice-to-have and block no workflow.
    useEffect(() => {
        const fetchGenera = async () => {
            try {
                const genera = await getGenera([0, 3]);
                const sorted = (list) => Array.from(new Set(list || []))
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b));
                setFrogGenera(sorted(genera['0']));
                setGeckoGenera(sorted(genera['3']));
            } catch (error) {
                console.warn('Genera fetch failed:', error?.message || error);
                setFrogGenera([]);
                setGeckoGenera([]);
            }
        };
        fetchGenera();
    }, []);

    // Legacy handleFacilitySelect removed - navigation now handled by FacilityConsoleDrawer

        // Cowork mobile sweep 2026-08-11: the cart/login icons lived ONLY
    // inside the Collapse, so closing the menu by default (see
    // collapseOnSelect note at the <BSNavbar>) would have hidden the cart
    // icon behind the hamburger on every phone. Same two-instance pattern
    // as NavbarSearch: one row inside the Collapse for desktop, one
    // always-rendered row on the brand line for phones; navbar.css
    // displays exactly one at any width (display:none keeps the hidden
    // twin out of the a11y tree and tab order).
    const renderNavIcons = (variantClass) => (
        <Nav className={`align-items-center navbar-icons-row ${variantClass}`}>
                            {/* Facility Console Access — operator nav.
                                Icon-only by operator preference;
                                discoverability is handled by tooltip
                                + aria-label rather than a visible
                                text label (text was tried 2026-06-23
                                and reverted 2026-06-29). */}
                            {/* Design System v1 §2.3: every header icon is ONE
                                ghost icon button — no coloured outlines, one
                                per icon, as there used to be. `nav-icon-btn`
                                stays for the tooltip hook and the phone row's
                                44px floor. R5 B5 (rule 8): EVERY header icon
                                is a ghost — 36px, an 18px icon (navbar.css) —
                                Sign up included: the header spends no filled
                                button, so the page's one primary is the
                                page's own. */}
                            {hasFacilityAccess && (
                                <button
                                    type="button"
                                    className="ff-btn ff-btn--ghost ff-btn--icon me-2 nav-icon-btn"
                                    onClick={toggleFacilityDrawer}
                                    aria-label="Facility console (operator menu)"
                                    aria-expanded={facilityDrawerOpen}
                                    data-tooltip="Facility console — Manage Products, Marketing, Operator Telemetry"
                                >
                                    <Settings size={18} />
                                </button>
                            )}

                            {/* FroggerAI dock toggle (facility routes,
                                desktop width — see FroggerDockButton). */}
                            <FroggerDockButton />

                            {/* R-NOTIFY §5.5 — the bell: the caller's own unread
                                count for the selected facility; opens the
                                list page. Absent without a facility. */}
                            {bellFacilityId && (
                                <NotificationBell facilityId={bellFacilityId} unread={unreadNotifications} />
                            )}

                            {/* Shopping Cart — the count is an accent dot. */}
                            <button
                                type="button"
                                className="ff-btn ff-btn--ghost ff-btn--icon position-relative me-2 nav-icon-btn"
                                onClick={() => navigate('/cart')}
                                aria-label="Cart"
                                data-tooltip="Cart"
                            >
                                <ShoppingCart size={18} />
                                {getTotalQuantity() > 0 && (
                                    <span className="nav-cart-count" data-cart-count>
                                        {getTotalQuantity()}
                                        <span className="visually-hidden">items in cart</span>
                                    </span>
                                )}
                            </button>

                            {/* Auth Buttons */}
                            {isAuthenticated ? (
                                <>
                                    <button
                                        type="button"
                                        className="ff-btn ff-btn--ghost ff-btn--icon me-2 nav-icon-btn"
                                        onClick={() => navigate('/account/orders')}
                                        aria-label="Profile"
                                        data-tooltip="Profile"
                                    >
                                        <UserRound size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        className="ff-btn ff-btn--ghost ff-btn--icon nav-icon-btn"
                                        onClick={logout}
                                        aria-label="Logout"
                                        data-tooltip="Logout"
                                    >
                                        <LogOut size={18} />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="ff-btn ff-btn--ghost ff-btn--icon me-2 nav-icon-btn"
                                        onClick={() => navigate('/login')}
                                        aria-label="Login"
                                        data-tooltip="Login"
                                    >
                                        <LogIn size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        className="ff-btn ff-btn--ghost ff-btn--icon nav-icon-btn"
                                        onClick={() => navigate('/signup')}
                                        aria-label="Sign up"
                                        data-tooltip="Sign up"
                                    >
                                        <UserRoundPlus size={18} />
                                    </button>
                                </>
                            )}
        </Nav>
    );

    return (
        <>
            {/* Cowork mobile sweep 2026-08-11: defaultExpanded shipped the
                hamburger menu OPEN on every phone (the collapse carried
                `show` on load — 235px of navbar, 28% of a 390x844 screen).
                Invisible on desktop, where >=lg always displays the
                collapse. collapseOnSelect closes the menu after a pick. */}
            <BSNavbar expand="lg" collapseOnSelect className="custom-navbar" fixed="top">
                <Container>
                    {/* Fix batch 43 (the operator's look at PROD, 2026-10-09):
                        from lg up the bar is a three-column grid — this START
                        side, the Frogs/Geckos/Plants group, the END side
                        (search + icons) — `1fr auto 1fr`, so the group sits on
                        the PAGE's centre whatever either side measures. It
                        used to be centred in the space LEFT OVER between a
                        ~150px logo side and a ~400px search side, ~145px left
                        of centre. Below lg both side wrappers are
                        `display: contents` (navbar.css), so the phone bar lays
                        out exactly as before. */}
                    <div className="navbar-side navbar-side--start">
                    {/* User Menu for Authenticated Users */}
                    {isAuthenticated && (
                        <div className="user-drawer-container me-3">
                            <button
                                type="button"
                                className="ff-btn ff-btn--ghost ff-btn--icon user-drawer-toggle nav-trigger-left"
                                onClick={() => setUserDrawerOpen(!userDrawerOpen)}
                                aria-label={userDrawerOpen ? "Close Account Menu" : "Open Account Menu"}
                                aria-expanded={userDrawerOpen}
                                aria-controls="user-account-drawer"
                                title="Account Menu"
                            >
                                <Menu size={18} />
                            </button>
                            
                            {/* User Drawer Overlay */}
                            {userDrawerOpen && (
                                <div className="user-drawer-overlay" onClick={() => setUserDrawerOpen(false)} />
                            )}
                            
                            {/* User Drawer */}
                            <div
                                ref={drawerRef}
                                id="user-account-drawer"
                                className={`user-drawer ${userDrawerOpen ? 'open' : ''}`}
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="user-drawer-title"
                                aria-hidden={!userDrawerOpen}
                            >
                                <div className="user-drawer-header">
                                    <h5 id="user-drawer-title" className="mb-0">My Account</h5>
                                    <button
                                        type="button"
                                        className="ff-btn ff-btn--ghost ff-btn--icon ff-btn--sm user-drawer-close"
                                        onClick={() => setUserDrawerOpen(false)}
                                        aria-label="Close Account Menu"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="user-drawer-content">
                                    <Nav className="flex-column">
                                        <Nav.Link 
                                            onClick={() => {
                                                navigate('/account/orders');
                                                setUserDrawerOpen(false);
                                            }}
                                            className="user-drawer-item"
                                        >
                                            Orders
                                        </Nav.Link>
                                        <Nav.Link 
                                            onClick={() => {
                                                navigate('/account/froggy-chat');
                                                setUserDrawerOpen(false);
                                            }}
                                            className="user-drawer-item"
                                        >
                                            FroggyAI
                                        </Nav.Link>
                                    </Nav>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Brand Logo */}
                    <BSNavbar.Brand as={Link} to="/" className="me-auto">
                        <img
                            src="/logos/ff original 500px.png"
                            alt="Feeling Froggy"
                            height="80"
                            className="d-inline-block align-top navbar-logo"
                        />
                    </BSNavbar.Brand>
                    </div>

                    {/* Main Navigation */}
                    <BSNavbar.Toggle aria-controls="basic-navbar-nav" />

                    {/* Cowork mobile sweep 2026-08-11: always-visible cart/
                        login row for phones — must sit BEFORE the full-width
                        mobile search in DOM order so flex-wrap keeps it on
                        the brand line instead of dropping it to row three. */}
                    {renderNavIcons('ff-navbar-icons--mobile')}

                    {/* #37b (2026-08-07) — mobile-reachable storefront search.
                        The desktop instance below lives INSIDE the Collapse,
                        which Bootstrap hides behind the hamburger under 992px,
                        so on phones search was only reachable via the menu.
                        This second instance sits OUTSIDE the Collapse and is
                        therefore always rendered; CSS shows it only below the
                        navbar's `lg` breakpoint, as its own full-width row
                        under the brand (the always-visible row has no room for
                        it inline at 360px). Hidden at lg+, so desktop layout
                        is byte-for-byte unchanged. */}
                    <NavbarSearch className="ff-navbar-search--mobile" />

                    <BSNavbar.Collapse id="basic-navbar-nav">
                        <Nav className="me-auto justify-content-center flex-grow-1 navbar-dropdowns-row">
                            <NavDropdown title="Frogs" id="frogs-dropdown" className="mx-2">
                                <NavDropdown.Item as={Link} to="/frogs">
                                    Available Now
                                </NavDropdown.Item>
                                <NavDropdown.Divider />
                                {frogGenera.map((genus) => (
                                    <NavDropdown.Item 
                                        key={genus}
                                        as={Link} 
                                        to={`/frogs/${genus.toLowerCase()}`}
                                    >
                                        {genus}
                                    </NavDropdown.Item>
                                ))}
                            </NavDropdown>

                            {/* S3-01.3 follow-up (2026-08-30): these two menus
                                were placeholder-only with NO link to their
                                pages — /geckos had a live listing nobody could
                                navigate to. Geckos now carries genus sub-links
                                (the Frogs pattern) via /geckos/:genus; Plants
                                gets them when plant genus pages exist. */}
                            <NavDropdown title="Geckos" id="geckos-dropdown" className="mx-2">
                                <NavDropdown.Item as={Link} to="/geckos">
                                    Available Now
                                </NavDropdown.Item>
                                <NavDropdown.Divider />
                                {geckoGenera.length > 0 ? (
                                    geckoGenera.map((genus) => (
                                        <NavDropdown.Item
                                            key={genus}
                                            as={Link}
                                            to={`/geckos/${genus.toLowerCase()}`}
                                        >
                                            {genus}
                                        </NavDropdown.Item>
                                    ))
                                ) : (
                                    <NavDropdown.Item disabled>
                                        More availability coming soon
                                    </NavDropdown.Item>
                                )}
                            </NavDropdown>

                            <NavDropdown title="Plants" id="plants-dropdown" className="mx-2">
                                <NavDropdown.Item as={Link} to="/plants">
                                    Available Now
                                </NavDropdown.Item>
                                <NavDropdown.Divider />
                                <NavDropdown.Item disabled>
                                    More availability coming soon
                                </NavDropdown.Item>
                            </NavDropdown>
                        </Nav>

                        {/* Fix batch 43: the grid's END column (see the START
                            side above). */}
                        <div className="navbar-side navbar-side--end">
                        {/* #37 storefront search (customer-facing).
                            Desktop instance — hidden below lg by CSS; the
                            always-visible mobile instance above takes over. */}
                        <NavbarSearch className="ff-navbar-search--desktop" />

                        {/* Right-aligned Icons */}
                        {renderNavIcons('ff-navbar-icons--desktop')}
                        </div>
                    </BSNavbar.Collapse>
                </Container>
            </BSNavbar>
            
            {/* The space a FIXED top bar would need — MEASURED (fix batch
                33, R11). It was a hard 80px "for a fixed navbar", but the
                navbar is sticky and in flow, so it was 80px of empty white
                above every page. `NavbarSpacer` is 0 for the bar we draw
                and the bar's own height if one is ever fixed. */}
            <NavbarSpacer />
            
            {/* Facility Console Drawer */}
            <FacilityConsoleDrawer />
        </>
    );
};

export default Navbar;
